import { useState } from 'react';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatApiErrorDetail = (detail, fallback) => {
  if (detail == null) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const msg = detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : '')).filter(Boolean).join(' ');
    return msg || fallback;
  }
  if (typeof detail.msg === 'string') return detail.msg;
  return fallback;
};

export const AuthForm = ({ initialMode = 'signin', onSuccess }) => {
  const { t } = useLanguage();
  const { setUser } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const endpoint = mode === 'signup' ? 'register' : 'login';
      const payload = mode === 'signup'
        ? { name: form.name.trim(), email: form.email.trim(), password: form.password, origin_url: window.location.origin }
        : { email: form.email.trim(), password: form.password };
      const res = await fetch(`${BACKEND_URL}/api/auth/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(formatApiErrorDetail(data.detail, t('authGenericError')));
        setBusy(false);
        return;
      }
      setUser(data);
      setBusy(false);
      onSuccess?.(data, mode);
    } catch {
      setError(t('authGenericError'));
      setBusy(false);
    }
  };

  return (
    <div data-testid="auth-form">
      <form onSubmit={submit} noValidate className="space-y-4">
        {mode === 'signup' && (
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('authNameLabel')}</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="form-input"
              placeholder="John Doe"
              data-testid="auth-name-input"
            />
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('authEmailLabel')}</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="form-input"
            placeholder="you@company.com"
            data-testid="auth-email-input"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('authPasswordLabel')}</label>
          <div className="relative">
            <input
              type={showPw ? 'text' : 'password'}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="form-input !pr-11"
              placeholder="••••••••"
              data-testid="auth-password-input"
            />
            <button
              type="button"
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
              onClick={() => setShowPw((s) => !s)}
              tabIndex={-1}
              data-testid="auth-password-toggle"
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {mode === 'signup' && <p className="mt-1.5 text-[11px] text-gray-600">{t('authPasswordHint')}</p>}
        </div>

        {error && (
          <p className="text-sm" style={{ color: '#fca5a5' }} data-testid="auth-error">{error}</p>
        )}

        <button
          type="submit"
          className="btn-accent w-full justify-center"
          disabled={busy}
          data-testid="auth-submit-btn"
        >
          {busy ? '...' : mode === 'signup' ? t('authSignUpBtn') : t('authSignInBtn')}
          {!busy && <ArrowRight size={15} />}
        </button>
      </form>

      <p className="mt-5 text-center text-sm text-gray-500">
        {mode === 'signin' ? t('authNoAccount') : t('authHaveAccount')}{' '}
        <button
          type="button"
          className="font-semibold transition-colors hover:opacity-80"
          style={{ color: '#e2c179' }}
          onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setError(''); }}
          data-testid="auth-toggle-mode"
        >
          {mode === 'signin' ? t('authSwitchSignUp') : t('authSwitchSignIn')}
        </button>
      </p>
    </div>
  );
};
